import { useEffect, useState } from 'react';
import {
  Card,
  Divider,
  NativeSelect,
  Skeleton,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import { getPatientInfo, getPatientBarcode } from 'apis/admin';
import useNotification from 'hooks/useNotification';
import useStudent from 'store/student/useStudent';
import usePatient from 'store/patient/usePatient';
import PatientInfoItem from './PatientInfoItem';
import useSurvey from 'store/survey/useSurvey';

const PatientInfo = () => {
  const { onFail } = useNotification();
  const { student_name: userName } = useStudent();
  const { patient, patientInfo, onSelectedPatientInfo } = usePatient();
  const { onUpdateSurveyType } = useSurvey();

  // 바코드 모달창 만들기
  const onClickModal = (label: string) => {
    onUpdateSurveyType(label);
  };

  // 부진단 코드 index
  const [diseaseSubIndex, setDiseaseSubIndex] = useState(0);

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientInfo({ patient_id: patient.patient_id })
      .then(({ data }) => {
        onSelectedPatientInfo(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  // 바코드 변수에 담기
  const [imgBarcode, setImgBarcode] = useState('');

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientBarcode({ patient_id: patient.patient_id })
      .then(({ data }) => {
        // console.log('바코드?', data);
        setImgBarcode(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  if (!patientInfo) {
    return <Skeleton variant="rectangular" sx={{ flex: 1 }} />;
  }
  const {
    // column1
    patient_id,
    name,
    age,
    // gender,
    blood,
    // column2
    height,
    weight,
    admin_hod,
    admin_pod,
    // column3
    department,
    ward,
    room,
    main_doctor,
    // column4
    disease_main,
    disease_sub,
  } = patientInfo;

  // 부진단 코드 선택 UI
  const DiseaseSubCode = () => (
    <NativeSelect
      value={diseaseSubIndex}
      onChange={e => setDiseaseSubIndex(Number(e.target.value))}
      inputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      sx={{ fontSize: 14, lineHeight: '18px' }}
    >
      {disease_sub?.map(({ disease_id }, index) => (
        <option key={disease_id} value={index}>
          {disease_id}
        </option>
      ))}
    </NativeSelect>
  );

  const convertHeight = (heightStr: string) => {
    // "160cm"에서 숫자만 추출
    const cmValue = parseFloat(heightStr);
    if (isNaN(cmValue)) return heightStr;
    // cm를 인치로 변환
    const totalInches = cmValue / 2.54;
    // 전체 인치에서 피트와 남은 인치 계산
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return `${cmValue}cm (${feet}’${inches}”)`;
  };

  const convertWeight = (weightStr: string) => {
    // "52kg"에서 숫자만 추출
    const kgValue = parseFloat(weightStr);
    if (isNaN(kgValue)) return weightStr;
    // kg를 파운드로 변환
    const pounds = kgValue * 2.20462;
    return `${kgValue}kg (${pounds.toFixed(1)} lbs)`;
  };

  return (
    <div style={{ zIndex: 1000 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" fontSize={13} mb={1}>
          {name} 患者資訊
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ marginBottom: '5px', marginTop: '-10px' }}
          onClick={() => onClickModal('病人标签打印')}
        >
          打印患者标签
        </Button>
      </div>
      <Card
        component="section"
        sx={{
          p: 2,
          display: 'flex',
          overflow: 'visible',
          justifyContent: 'space-around',
          gap: { xs: 1.25, xl: 2.5 },
        }}
      >
        <Stack spacing={1.25}>
          <PatientInfoItem title="登記號碼" content={patient_id} />
          <PatientInfoItem title="姓名" content={name} />
          <PatientInfoItem title="年齡" content={age} />
          {/* <PatientInfoItem title="Gender" content={i18n(`GENDER.${gender}`)} /> */}
          <PatientInfoItem title="血型" content={blood} />
        </Stack>
        <Divider orientation="horizontal" />
        <Stack spacing={1.25}>
          <PatientInfoItem title="身高" content={`${height} cm`} />
          <PatientInfoItem title="體重" content={`${weight} kg`} />
          <PatientInfoItem title="住院日" content={admin_hod} />
          <PatientInfoItem title="術後日" content={admin_pod} />
        </Stack>
        <Divider orientation="horizontal" />
        <Stack spacing={1.25}>
          <PatientInfoItem title="科室" content={department} />
          <PatientInfoItem title="病房" content={ward} />
          <PatientInfoItem title="房間" content={room} />
          <PatientInfoItem title="主責護理師" content={userName} />
          <PatientInfoItem title="主治醫師" content={main_doctor} />
        </Stack>
        <Divider orientation="horizontal" />
        <Stack spacing={1.25}>
          <PatientInfoItem title="代碼" content={disease_main.disease_id} />
          <PatientInfoItem title="診斷" content={disease_main.disease_eng} />
          <PatientInfoItem
            title="代碼"
            content={disease_sub.length > 0 ? <DiseaseSubCode /> : null}
          />
          <PatientInfoItem
            title="次診斷"
            content={disease_sub[diseaseSubIndex]?.disease_eng}
          />
        </Stack>
      </Card>
    </div>
  );
};

export default PatientInfo;
